"use client"

import * as React from "react"
import { Check, Minus, Sparkles } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { LocationPill } from "@/components/ui/flag"
import { Price, PriceStrike } from "@/components/ui/price"
import { Reveal } from "@/components/motion/reveal"
import { cn } from "@/lib/utils"
import { priceForCycle, formatPrice, toFaDigits, type BillingCycle } from "@/lib/format"
import { resolveBuyUrl } from "@/lib/whmcs"
import type { PlanFeature } from "@/types/content"

export interface PricingCardPlan {
  id: string
  title: string
  slug: string
  description?: string | null
  price: number
  currency: import("@/lib/format").Currency
  annualDiscount?: number | null
  quarterlyDiscount?: number | null
  features: PlanFeature[]
  location?: import("@/types/content").Location | null
  featured?: boolean
  badge?: string | null
  whmcsProductId?: number | null
  whmcsCartUrl?: string | null
  /** Rendered above the feature list — the headline spec row. */
  specs?: { label: string; value: string }[]
  comingSoon?: boolean
  priceUsd?: number | null
  /** VPS cards: hide the USD equivalent under the toman price. */
  hideUsd?: boolean
}

export function PricingCard({
  plan,
  cycle,
  className,
}: {
  plan: PricingCardPlan
  cycle: BillingCycle
  className?: string
}) {
  const discount = plan.annualDiscount ?? 0
  const quarterly = plan.quarterlyDiscount ?? 0
  const total = priceForCycle(plan.price, cycle, discount, quarterly)
  const undiscounted = priceForCycle(plan.price, cycle, 0, 0)
  const hasDiscount =
    (cycle === "quarterly" && quarterly > 0) ||
    (cycle !== "monthly" && cycle !== "quarterly" && discount > 0)

  const buyUrl = resolveBuyUrl(plan, cycle)
  const comingSoon = plan.comingSoon === true

  return (
    <Reveal
      standalone={false}
      as="li"
      className={cn("h-full list-none", className)}
      variants={{
        hidden: { opacity: 0, y: 22 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        },
      }}
    >
      <article
        className={cn(
          "group relative flex h-full flex-col overflow-hidden rounded-[var(--radius-lg)]",
          "surface-lift",
          plan.featured
            ? "border border-brand-600 bg-[var(--surface)] shadow-glow"
            : "border border-[var(--hairline-strong)] bg-[var(--surface)] shadow-sm hover:border-brand-500/50",
        )}
      >
        {plan.featured ? (
          <span
            className="absolute inset-x-0 top-0 h-1 bg-linear-to-l from-accent-400 via-brand-500 to-iris-500"
            aria-hidden
          />
        ) : null}

        <div className="relative flex flex-1 flex-col p-6">
          <header>
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-lg font-bold text-[var(--content)]">{plan.title}</h3>
              {comingSoon ? (
                <Badge variant="warning" size="sm">
                  به زودی
                </Badge>
              ) : plan.badge ? (
                <Badge variant={plan.featured ? "solid" : "brand"} size="sm">
                  {plan.featured ? <Sparkles aria-hidden /> : null}
                  {plan.badge}
                </Badge>
              ) : null}
            </div>

            {plan.description ? (
              <p className="mt-2.5 min-h-11 text-[0.8125rem] leading-[var(--leading-snug)] text-[var(--content-muted)]">
                {plan.description}
              </p>
            ) : null}
          </header>

          <div className="mt-5 rounded-[var(--radius-md)] px-3.5 py-3">
            {comingSoon ? (
              <>
                <p className="text-xl font-extrabold tracking-[-0.015em] text-[var(--content)]">نامعلوم</p>
                <p className="mt-1.5 min-h-6 text-[0.6875rem] text-[var(--content-subtle)]">به زودی</p>
              </>
            ) : plan.price <= 0 ? (
              <>
                <p className="text-xl font-extrabold tracking-[-0.015em] text-[var(--content)]">با هماهنگی</p>
                <p className="mt-1.5 min-h-6 text-[0.6875rem] text-[var(--content-subtle)]">تماس یا تیکت</p>
              </>
            ) : (
              <>
                <Price amount={total} currency={plan.currency} cycle={cycle} size="md" />
                {plan.priceUsd != null && !plan.hideUsd ? (
                  <p className="mt-1 text-[0.6875rem] text-[var(--content-subtle)]">
                    معادل {formatPrice(priceForCycle(plan.priceUsd, cycle, discount), "USD")}
                  </p>
                ) : null}
                <p
                  aria-hidden={!hasDiscount}
                  className={cn(
                    "mt-1.5 flex min-h-6 items-center gap-2",
                    hasDiscount ? "visible" : "invisible",
                  )}
                >
                  <PriceStrike amount={undiscounted} currency={plan.currency} />
                  <Badge variant="success" size="sm">
                    صرفه‌جویی {toFaDigits(cycle === "annually" ? 3.5 : Math.round((cycle === "quarterly" ? quarterly : discount) * 100))}٪
                  </Badge>
                </p>
              </>
            )}
          </div>

          {plan.location && plan.slug && !plan.slug.startsWith("linux-") ? (
            <div className="mt-4">
              <LocationPill location={plan.location} showLatency />
            </div>
          ) : plan.location && plan.slug?.startsWith("linux-") && plan.slug !== "linux-custom" ? (
            <div className="mt-4">
              <LocationPill location={plan.location} />
            </div>
          ) : null}

          {plan.specs?.length ? (
            <dl className="mt-5 grid grid-cols-2 gap-3 rounded-[var(--radius-md)] bg-[var(--canvas-subtle)] p-3.5">
              {plan.specs.map((spec) => (
                <div key={spec.label}>
                  <dt className="text-[0.625rem] font-semibold tracking-[0.04em] text-[var(--content-subtle)]">
                    {spec.label}
                  </dt>
                  <dd className="mt-0.5 text-[0.8125rem] font-bold text-[var(--content)]">
                    {comingSoon ? (
                      <span className="inline-block select-none blur-[7px]" aria-hidden>
                        {spec.value}
                      </span>
                    ) : (
                      spec.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}

          <ul className="mt-5 flex-1 space-y-2.5">
            {plan.features.map((feature) => (
              <li
                key={feature.label}
                className={cn(
                  "flex items-start gap-2.5 text-[0.8125rem] leading-[var(--leading-snug)]",
                  feature.included === false
                    ? "text-[var(--content-subtle)]"
                    : "text-[var(--content-muted)]",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full",
                    feature.included === false
                      ? "bg-[var(--hairline)] text-[var(--content-subtle)]"
                      : feature.highlight
                        ? "bg-brand-500 text-white"
                        : "bg-success-500/15 text-success-600 dark:text-success-400",
                  )}
                  aria-hidden
                >
                  {feature.included === false ? (
                    <Minus className="size-2.5" />
                  ) : (
                    <Check className="size-2.5" strokeWidth={3.5} />
                  )}
                </span>
                <span className={cn(feature.highlight && "font-semibold text-[var(--content)]")}>
                  {comingSoon ? (
                    <span className="inline-block select-none blur-[7px]" aria-hidden>
                      {feature.label}
                    </span>
                  ) : (
                    feature.label
                  )}
                  {feature.hint && !comingSoon ? (
                    <span className="block text-[0.6875rem] text-[var(--content-subtle)]">
                      {feature.hint}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-6">
            {comingSoon ? (
              <Button block size="lg" variant="secondary" disabled>
                به زودی
              </Button>
            ) : plan.price <= 0 ? (
              <div className="grid gap-2">
                <Button asChild block size="lg" variant="secondary">
                  <a href="/contact">تماس بگیرید</a>
                </Button>
                <Button asChild block size="lg" variant="primary">
                  <a href={buyUrl} target="_blank" rel="noopener noreferrer">
                    تیکت بزنید
                  </a>
                </Button>
              </div>
            ) : (
              <Button
                asChild
                block
                size="lg"
                variant={plan.featured ? "primary" : "secondary"}
              >
                <a href={buyUrl} target="_blank" rel="noopener noreferrer">
                  سفارش {plan.title}
                </a>
              </Button>
            )}
          </div>
        </div>
      </article>
    </Reveal>
  )
}
